import axios from 'axios';

// Single-origin in production builds and under the Vite dev proxy; the
// absolute URL only applies when the API is served elsewhere (VITE_API_URL).
const API_URL = import.meta.env.PROD
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'http://localhost:5000/api');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    // Failed login/setup attempts must stay on the page and show the error
    // toast, not trigger a full app redirect (401 here means "bad credentials").
    const isAuthAttempt = /\/auth\/(login|setup|register)$/.test(requestUrl);
    if (error.response?.status === 401 && !isAuthAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  logout: () => api.post('/auth/logout'),
  getUsers: () => api.get('/auth/users'),
  getUserById: (id) => api.get(`/auth/users/${id}`),
  updateUserStatus: (id, isActive) => api.put(`/auth/users/${id}/status`, { isActive }),
  updateUserRole: (id, role) => api.put(`/auth/users/${id}/role`, { role }),
  updateUserPermissions: (id, permissions) => api.put(`/auth/users/${id}/permissions`, { permissions }),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
  getPermissionsList: () => api.get('/auth/permissions/list'),
  setup: (data) => api.post('/auth/setup', data),
  checkSetup: () => api.get('/auth/setup/status'),
};

// Clients API
export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  getInvoices: (id, params) => api.get(`/clients/${id}/invoices`, { params }),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getStats: (id) => api.get(`/clients/${id}/stats`),
};

// Items API
export const itemsAPI = {
  getAll: (params) => api.get('/items', { params }),
  getById: (id) => api.get(`/items/${id}`),
  getCategories: () => api.get('/items/categories'),
  create: (data) => api.post('/items', data),
  update: (id, data) => api.put(`/items/${id}`, data),
  delete: (id) => api.delete(`/items/${id}`),
  bulkCreate: (items) => api.post('/items/bulk', { items }),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: (id) => api.post(`/invoices/${id}/send`),
  sendEmail: (id) => api.post(`/invoices/${id}/email`),
  cancel: (id, reason) => api.post(`/invoices/${id}/cancel`, { reason }),
  duplicate: (id) => api.post(`/invoices/${id}/duplicate`),
  getPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  createStripeIntent: (invoiceId) => api.post('/payments/stripe/create-intent', { invoiceId }),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  get: (key) => api.get(`/settings/${key}`),
  update: (key, value, type) => api.put(`/settings/${key}`, { value, type }),
  bulkUpdate: (settings) => api.put('/settings', { settings }),
  delete: (key) => api.delete(`/settings/${key}`),
  uploadLogo: (formData) => api.post('/settings/upload-logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  // Units management
  getUnits: () => api.get('/settings/units/all'),
  createUnit: (data) => api.post('/settings/units', data),
  updateUnit: (id, data) => api.put(`/settings/units/${id}`, data),
  deleteUnit: (id) => api.delete(`/settings/units/${id}`),
  // Bank accounts management
  getBankAccounts: () => api.get('/settings/bank-accounts/all'),
  createBankAccount: (data) => api.post('/settings/bank-accounts', data),
  updateBankAccount: (id, data) => api.put(`/settings/bank-accounts/${id}`, data),
  deleteBankAccount: (id) => api.delete(`/settings/bank-accounts/${id}`),
  setDefaultBankAccount: (id) => api.put(`/settings/bank-accounts/${id}/default`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRevenue: (year) => api.get('/dashboard/revenue', { params: { year } }),
  getRecentInvoices: (limit) => api.get('/dashboard/recent-invoices', { params: { limit } }),
  getOverdue: (limit) => api.get('/dashboard/overdue-invoices', { params: { limit } }),
  getTopClients: (limit, period) => api.get('/dashboard/top-clients', { params: { limit, period } }),
  getInvoiceBreakdown: () => api.get('/dashboard/invoice-breakdown'),
  getPaymentBreakdown: (startDate, endDate) => api.get('/dashboard/payment-breakdown', { params: { startDate, endDate } }),
  getActivity: (days) => api.get('/dashboard/activity', { params: { days } }),
};

// Quotations API
export const quotationsAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  getById: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  send: (id) => api.post(`/quotations/${id}/send`),
  sendEmail: (id) => api.post(`/quotations/${id}/email`),
  duplicate: (id) => api.post(`/quotations/${id}/duplicate`),
  convert: (id) => api.post(`/quotations/${id}/convert`),
  accept: (id) => api.post(`/quotations/${id}/accept`),
  reject: (id, reason) => api.post(`/quotations/${id}/reject`, { reason }),
  getPDF: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),
};

// Public API (no auth required)
export const publicAPI = {
  getInvoice: (token) => api.get(`/public/invoice/${token}`),
  getInvoicePDF: (token) => api.get(`/public/invoice/${token}/pdf`, { responseType: 'blob' }),
  createPayment: (token) => api.post(`/public/invoice/${token}/payment`),
  getPaymentStatus: (token, paymentIntent) => api.get(`/public/invoice/${token}/payment-status`, { params: { payment_intent: paymentIntent } }),
  getQuotation: (token) => api.get(`/public/quotation/${token}`),
  getQuotationPDF: (token) => api.get(`/public/quotation/${token}/pdf`, { responseType: 'blob' }),
  acceptQuotation: (token) => api.post(`/public/quotation/${token}/accept`),
  rejectQuotation: (token, reason) => api.post(`/public/quotation/${token}/reject`, { reason }),
  requestQuotationChange: (token, message) => api.post(`/public/quotation/${token}/request-change`, { message }),
};

export default api;
